import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { DashboardFooter } from "../dashboard/layout/DashboardFooter";
import { DashboardHeader } from "../dashboard/layout/DashboardHeader";
import { zeosSuccess } from "../../components/Shared/confirm-dialog/ConfirmDialog";
import { AuthContext } from "../../context/AuthContext";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { createLogger } from "../../services/Logger";
import {
  INotificationsSettingsRequest,
  IPreferencesSettingsRequest,
  IProfileSettingsRequest,
  ISettingsResponse,
} from "../../services/zeos-api/contracts/Settings";
import { ICompanyContract } from "../../services/zeos-api/contracts/Company";
import { IPropertyContract } from "../../services/zeos-api/contracts/Property";
import { AdminPanel } from "./admin/AdminPanel";

type SectionKey =
  | "profile"
  | "account"
  | "security"
  | "notifications"
  | "preferences"
  | "company"
  | "properties"
  | "utility"
  | "integrations"
  | "billing"
  | "support"
  | "admin";

interface IProps {
  defaultSection?: SectionKey;
}

type StatusState = {
  type: "success" | "error";
  message: string;
};

type CompanyFormState = {
  companyId: number;
  companyName: string;
  companyTypeId?: number;
  customerId?: number;
  address: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  website: string;
  canEdit: boolean;
};

type PropertyFormState = {
  propertyId: number;
  companyId: number;
  propertyName: string;
  propertyTypeId: number;
  buildingTypeId?: number;
  landTypeId?: number;
  address: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  units: string;
  floors: string;
  sqFt: string;
  acreage: string;
  yearBuilt: string;
  roofSize: string;
  paysBill: string;
  isWifiAvailable?: boolean;
  bbl: string;
  bin: string;
  county: string;
  latitude: string;
  longitude: string;
};

const sectionLabels: Record<SectionKey, string> = {
  profile: "Profile",
  account: "Account",
  security: "Security",
  notifications: "Notifications",
  preferences: "Preferences",
  company: "Company",
  properties: "Properties",
  utility: "Utilities",
  integrations: "Integrations",
  billing: "Billing",
  support: "Support",
  admin: "Administration",
};

const mailtoAddress = (url: string): string | null => {
  const match = /^mailto:([^?]+)/i.exec(url.trim());
  return match ? decodeURIComponent(match[1]) : null;
};

export const SettingsScreen = ({ defaultSection = "profile" }: IProps) => {
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const log = useMemo(() => createLogger("SettingsScreen"), []);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSectionParam = searchParams.get("section");
  const initialSection =
    initialSectionParam && initialSectionParam in sectionLabels
      ? (initialSectionParam as SectionKey)
      : defaultSection;
  const [activeSection, setActiveSection] = useState<SectionKey>(initialSection);
  const [settings, setSettings] = useState<ISettingsResponse | null>(null);
  const [companyForms, setCompanyForms] = useState<CompanyFormState[]>([]);
  const [propertyForms, setPropertyForms] = useState<PropertyFormState[]>([]);
  const [profileForm, setProfileForm] = useState<IProfileSettingsRequest>({
    firstName: "",
    lastName: "",
    title: "",
    phone: "",
    timezone: "",
  });
  const [notificationsForm, setNotificationsForm] =
    useState<INotificationsSettingsRequest>({
      notifyEmail: true,
      notifyInApp: true,
      notifyFrequency: "daily",
      quietHoursStart: "",
      quietHoursEnd: "",
    });
  const [preferencesForm, setPreferencesForm] =
    useState<IPreferencesSettingsRequest>({
      units: "imperial",
      dateFormat: "MM/DD/YYYY",
      language: "en-US",
    });
  const [status, setStatus] = useState<StatusState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const useSuperAdminLayout = isSuperAdmin === true;

  const sections = useMemo(() => {
    return Object.keys(sectionLabels)
      .filter((key) => key !== "properties")
      .filter((key) => (key === "admin" ? isSuperAdmin === true : true))
      .map((key) => ({
        key: key as SectionKey,
        label: sectionLabels[key as SectionKey],
      }));
  }, [isSuperAdmin]);

  useEffect(() => {
    const loadAuth = async () => {
      const user = await auth.zeosUserAsync();
      setIsSuperAdmin(Boolean(user?.isSuperAdmin));
    };
    loadAuth();
  }, [auth]);

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      const response = await api.Settings.getSettingsAsync();
      log.info("Settings load response", {
        status: response?.zeosStatus,
        message: response?.message,
        hasData: Boolean(response?.data),
      });
      if (response?.data) {
        setSettings(response.data);
        setStatus(null);
        setProfileForm({
          firstName: response.data.profile.firstName ?? "",
          lastName: response.data.profile.lastName ?? "",
          title: response.data.profile.title ?? "",
          phone: response.data.profile.phone ?? "",
          timezone: response.data.profile.timezone ?? "",
        });
        setCompanyForms(
          (response.data.company?.companies ?? []).map((company) => ({
            companyId: company.companyId,
            companyName: company.companyName ?? "",
            companyTypeId: company.companyTypeId,
            customerId: company.customerId,
            address: company.address ?? "",
            suite: company.suite ?? "",
            city: company.city ?? "",
            state: company.state ?? "",
            zip: company.zip ?? "",
            website: company.website ?? "",
            canEdit: company.canEdit === true,
          }))
        );
        setPropertyForms(
          (response.data.properties?.properties ?? []).map((property) => ({
            propertyId: property.propertyId,
            companyId: property.companyId,
            propertyName: property.propertyName ?? "",
            propertyTypeId: property.propertyTypeId,
            buildingTypeId: property.buildingTypeId,
            landTypeId: property.landTypeId,
            address: property.address ?? "",
            suite: property.suite ?? "",
            city: property.city ?? "",
            state: property.state ?? "",
            zip: property.zip ?? "",
            units: property.units?.toString() ?? "",
            floors: property.floors?.toString() ?? "",
            sqFt: property.sqFt?.toString() ?? "",
            acreage: property.acreage?.toString() ?? "",
            yearBuilt: property.yearBuilt?.toString() ?? "",
            roofSize: property.roofSize?.toString() ?? "",
            paysBill: property.paysBill ?? "",
            isWifiAvailable: property.isWifiAvailable,
            bbl: property.bbl ?? "",
            bin: property.bin ?? "",
            county: property.county ?? "",
            latitude: property.latitude?.toString() ?? "",
            longitude: property.longitude?.toString() ?? "",
          }))
        );
        setNotificationsForm({
          notifyEmail: response.data.notifications.notifyEmail,
          notifyInApp: response.data.notifications.notifyInApp,
          notifyFrequency: response.data.notifications.notifyFrequency ?? "daily",
          quietHoursStart: response.data.notifications.quietHoursStart ?? "",
          quietHoursEnd: response.data.notifications.quietHoursEnd ?? "",
        });
        setPreferencesForm({
          units: response.data.preferences.units ?? "imperial",
          dateFormat: response.data.preferences.dateFormat ?? "MM/DD/YYYY",
          language: response.data.preferences.language ?? "en-US",
        });
      } else {
        log.error("Failed to load settings", response?.message);
        setStatus({
          type: "error",
          message: "Unable to load settings.",
        });
      }
      setIsLoading(false);
    };
    loadSettings();
  }, [api, log]);

  const updateCompanyField = (
    companyId: number,
    field: keyof CompanyFormState,
    value: string | number | undefined
  ) => {
    setCompanyForms((prev) =>
      prev.map((company) =>
        company.companyId === companyId
          ? { ...company, [field]: value }
          : company
      )
    );
  };

  const updatePropertyField = (
    propertyId: number,
    field: keyof PropertyFormState,
    value: string | number | boolean | undefined
  ) => {
    setPropertyForms((prev) =>
      prev.map((property) =>
        property.propertyId === propertyId
          ? { ...property, [field]: value }
          : property
      )
    );
  };

  const parseNumber = (value: string) => {
    if (!value || value.trim() === "") {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const saveCompany = async (companyId: number) => {
    const company = companyForms.find((item) => item.companyId === companyId);
    if (!company) {
      return;
    }

    setIsSaving(true);
    const payload: ICompanyContract = {
      companyId: company.companyId,
      companyName: company.companyName,
      companyTypeId: company.companyTypeId,
      customerId: company.customerId,
      address: company.address,
      suite: company.suite,
      city: company.city,
      state: company.state,
      zip: company.zip,
      website: company.website,
    };

    const response = await api.Company.putCompanyAsync(payload);
    if (response?.message !== "error") {
      await zeosSuccess("Company updated.", () => {
        setSettings((prev) =>
          prev
            ? {
                ...prev,
                company: {
                  ...prev.company,
                  companies: prev.company.companies.map((item) =>
                    item.companyId === company.companyId
                      ? {
                          ...item,
                          companyName: company.companyName,
                          companyTypeId: company.companyTypeId,
                          customerId: company.customerId,
                          address: company.address,
                          suite: company.suite,
                          city: company.city,
                          state: company.state,
                          zip: company.zip,
                          website: company.website,
                        }
                      : item
                  ),
                },
              }
            : prev
        );
      });
    } else {
      setStatus({ type: "error", message: "Company update failed." });
    }
    setIsSaving(false);
  };

  const saveProperty = async (propertyId: number) => {
    const property = propertyForms.find((item) => item.propertyId === propertyId);
    if (!property) {
      return;
    }

    setIsSaving(true);
    const payload: IPropertyContract = {
      propertyId: property.propertyId,
      companyId: property.companyId,
      propertyName: property.propertyName,
      propertyTypeId: property.propertyTypeId,
      buildingTypeId: property.buildingTypeId,
      landTypeId: property.landTypeId,
      address: property.address,
      suite: property.suite,
      city: property.city,
      state: property.state,
      zip: property.zip,
      units: parseNumber(property.units),
      floors: parseNumber(property.floors),
      sqFt: parseNumber(property.sqFt),
      acreage: parseNumber(property.acreage),
      yearBuilt: parseNumber(property.yearBuilt),
      roofSize: parseNumber(property.roofSize),
      paysBill: property.paysBill,
      isWifiAvailable: property.isWifiAvailable,
      bbl: property.bbl,
      bin: property.bin,
      county: property.county,
      latitude: parseNumber(property.latitude),
      longitude: parseNumber(property.longitude),
      projects: [],
    };

    const response = await api.Property.putProperty(payload);
    if (response?.message !== "error") {
      await zeosSuccess("Property updated.", () => {
        setSettings((prev) =>
          prev
            ? {
                ...prev,
                properties: {
                  ...prev.properties,
                  properties: prev.properties.properties.map((item) =>
                    item.propertyId === property.propertyId
                      ? {
                          ...item,
                          propertyName: property.propertyName,
                          address: property.address,
                          suite: property.suite,
                          city: property.city,
                          state: property.state,
                          zip: property.zip,
                          units: parseNumber(property.units),
                          floors: parseNumber(property.floors),
                          sqFt: parseNumber(property.sqFt),
                          acreage: parseNumber(property.acreage),
                          yearBuilt: parseNumber(property.yearBuilt),
                          roofSize: parseNumber(property.roofSize),
                          paysBill: property.paysBill,
                          isWifiAvailable: property.isWifiAvailable,
                          bbl: property.bbl,
                          bin: property.bin,
                        }
                      : item
                  ),
                },
              }
            : prev
        );
      });
    } else {
      setStatus({ type: "error", message: "Property update failed." });
    }
    setIsSaving(false);
  };

  const handleSectionChange = (section: SectionKey) => {
    setActiveSection(section);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("section", section);
    setSearchParams(nextParams, { replace: true });
  };

  const saveProfile = async () => {
    setIsSaving(true);
    const response = await api.Settings.updateProfileAsync(profileForm);
    if (response?.data) {
      setSettings((prev) =>
        prev ? { ...prev, profile: response.data } : prev
      );
      setStatus({ type: "success", message: "Profile updated." });
    } else {
      setStatus({ type: "error", message: "Profile update failed." });
    }
    setIsSaving(false);
  };

  const saveNotifications = async () => {
    setIsSaving(true);
    const response = await api.Settings.updateNotificationsAsync(
      notificationsForm
    );
    if (response?.data) {
      setSettings((prev) =>
        prev ? { ...prev, notifications: response.data } : prev
      );
      setStatus({ type: "success", message: "Notifications updated." });
    } else {
      setStatus({ type: "error", message: "Notification update failed." });
    }
    setIsSaving(false);
  };

  const savePreferences = async () => {
    setIsSaving(true);
    const response = await api.Settings.updatePreferencesAsync(preferencesForm);
    if (response?.data) {
      setSettings((prev) =>
        prev ? { ...prev, preferences: response.data } : prev
      );
      setStatus({ type: "success", message: "Preferences updated." });
    } else {
      setStatus({ type: "error", message: "Preferences update failed." });
    }
    setIsSaving(false);
  };

  const requestPasswordReset = async () => {
    setIsSaving(true);
    const response = await api.Settings.requestPasswordResetAsync();
    if (response?.zeosStatus === "Success" || response?.zeosStatus === "success") {
      setStatus({
        type: "success",
        message: "Password reset email sent.",
      });
    } else {
      setStatus({
        type: "error",
        message: "Password reset request failed.",
      });
    }
    setIsSaving(false);
  };

  const renderStatus = () => {
    if (!status) return null;
    const baseClass =
      status.type === "error"
        ? "text-red-700 bg-red-50 border-red-200"
        : "text-green-700 bg-green-50 border-green-200";
    return (
      <div
        className={`border rounded p-2 text-xs ${baseClass}`}
        data-testid="settings-status"
      >
        {status.message}
      </div>
    );
  };

  const renderProfileSection = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <InputField
          label="First name"
          value={profileForm.firstName}
          testId="profile-first-name"
          onChange={(value) => setProfileForm({ ...profileForm, firstName: value })}
        />
        <InputField
          label="Last name"
          value={profileForm.lastName}
          testId="profile-last-name"
          onChange={(value) => setProfileForm({ ...profileForm, lastName: value })}
        />
      </div>
      <InputField
        label="Title/role"
        value={profileForm.title ?? ""}
        testId="profile-title"
        onChange={(value) => setProfileForm({ ...profileForm, title: value })}
      />
      <div className="grid grid-cols-2 gap-4">
        <InputField
          label="Phone"
          value={profileForm.phone ?? ""}
          testId="profile-phone"
          onChange={(value) => setProfileForm({ ...profileForm, phone: value })}
        />
        <InputField
          label="Timezone"
          value={profileForm.timezone ?? ""}
          testId="profile-timezone"
          onChange={(value) => setProfileForm({ ...profileForm, timezone: value })}
        />
      </div>
      <InputField
        label="Email (read-only)"
        value={settings?.profile.emailAddress ?? ""}
        onChange={() => {}}
        disabled
      />
      <PrimaryButton
        onClick={saveProfile}
        disabled={isSaving}
        testId="profile-save-button"
      >
        Save profile
      </PrimaryButton>
    </div>
  );

  const renderAccountSection = () => (
    <div className="space-y-3">
      <ReadOnlyField label="Username" value={settings?.account.userName} mask />
      <ReadOnlyField label="Email" value={settings?.account.emailAddress} mask />
      <ReadOnlyField
        label="Last login"
        value={settings?.account.lastLoginDate}
      />
      <div className="text-xs text-gray-500">
        Account changes are managed through support.
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-4">
      <ReadOnlyField
        label="Password reset email"
        value={settings?.security.passwordResetEmail}
        mask
      />
      <div className="text-xs text-gray-500">
        {settings?.security.statusMessage}
      </div>
      <PrimaryButton onClick={requestPasswordReset} disabled={isSaving}>
        Send password reset email
      </PrimaryButton>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="space-y-4">
      <ToggleField
        label="Email notifications"
        checked={notificationsForm.notifyEmail}
        onChange={(checked) =>
          setNotificationsForm({ ...notificationsForm, notifyEmail: checked })
        }
      />
      <ToggleField
        label="In-app notifications"
        checked={notificationsForm.notifyInApp}
        onChange={(checked) =>
          setNotificationsForm({ ...notificationsForm, notifyInApp: checked })
        }
      />
      <InputField
        label="Frequency"
        value={notificationsForm.notifyFrequency ?? ""}
        onChange={(value) =>
          setNotificationsForm({ ...notificationsForm, notifyFrequency: value })
        }
      />
      <div className="grid grid-cols-2 gap-4">
        <InputField
          label="Quiet hours start"
          value={notificationsForm.quietHoursStart ?? ""}
          onChange={(value) =>
            setNotificationsForm({ ...notificationsForm, quietHoursStart: value })
          }
        />
        <InputField
          label="Quiet hours end"
          value={notificationsForm.quietHoursEnd ?? ""}
          onChange={(value) =>
            setNotificationsForm({ ...notificationsForm, quietHoursEnd: value })
          }
        />
      </div>
      <PrimaryButton onClick={saveNotifications} disabled={isSaving}>
        Save notifications
      </PrimaryButton>
    </div>
  );

  const renderPreferencesSection = () => (
    <div className="space-y-4">
      <InputField
        label="Units"
        value={preferencesForm.units ?? ""}
        onChange={(value) => setPreferencesForm({ ...preferencesForm, units: value })}
      />
      <InputField
        label="Date format"
        value={preferencesForm.dateFormat ?? ""}
        onChange={(value) =>
          setPreferencesForm({ ...preferencesForm, dateFormat: value })
        }
      />
      <InputField
        label="Language"
        value={preferencesForm.language ?? ""}
        onChange={(value) =>
          setPreferencesForm({ ...preferencesForm, language: value })
        }
      />
      <PrimaryButton onClick={savePreferences} disabled={isSaving}>
        Save preferences
      </PrimaryButton>
    </div>
  );

  const renderCompanySection = () => (
    <div className="space-y-4">
      {companyForms.map((company) => (
        <div key={company.companyId} className="border rounded p-3 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="Company name"
              value={company.companyName}
              onChange={(value) =>
                updateCompanyField(company.companyId, "companyName", value)
              }
              disabled={!company.canEdit}
            />
            <InputField
              label="Website"
              value={company.website}
              onChange={(value) =>
                updateCompanyField(company.companyId, "website", value)
              }
              disabled={!company.canEdit}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="Address"
              value={company.address}
              onChange={(value) =>
                updateCompanyField(company.companyId, "address", value)
              }
              disabled={!company.canEdit}
            />
            <InputField
              label="Suite"
              value={company.suite}
              onChange={(value) =>
                updateCompanyField(company.companyId, "suite", value)
              }
              disabled={!company.canEdit}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <InputField
              label="City"
              value={company.city}
              onChange={(value) =>
                updateCompanyField(company.companyId, "city", value)
              }
              disabled={!company.canEdit}
            />
            <InputField
              label="State"
              value={company.state}
              onChange={(value) =>
                updateCompanyField(company.companyId, "state", value)
              }
              disabled={!company.canEdit}
            />
            <InputField
              label="Zip"
              value={company.zip}
              onChange={(value) =>
                updateCompanyField(company.companyId, "zip", value)
              }
              disabled={!company.canEdit}
            />
          </div>
          {company.canEdit ? (
            <PrimaryButton
              onClick={() => saveCompany(company.companyId)}
              disabled={isSaving}
            >
              Save company
            </PrimaryButton>
          ) : (
            <div className="text-xs text-gray-500">
              Only a company Admin can edit this company.
            </div>
          )}
        </div>
      ))}
      {companyForms.length === 0 && (
        <div className="text-xs text-gray-500">No company data available.</div>
      )}
    </div>
  );

  const renderPropertiesSection = () => (
    <div className="space-y-4">
      {propertyForms.map((property) => {
        const propertyMeta = settings?.properties.properties.find(
          (item) => item.propertyId === property.propertyId
        );
        return (
          <div key={property.propertyId} className="border rounded p-3 space-y-3">
            <div className="text-sm font-semibold text-gray-900">
              {property.propertyName || "Property"}
            </div>
            <div className="text-xs text-gray-500">
              {propertyMeta?.propertyTypeName ?? "Property type"}{" "}
              {propertyMeta?.buildingTypeName
                ? `• ${propertyMeta.buildingTypeName}`
                : ""}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Property name"
                value={property.propertyName}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "propertyName", value)
                }
              />
              <InputField
                label="Address"
                value={property.address}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "address", value)
                }
              />
            </div>
            <InputField
              label="Suite"
              value={property.suite}
              onChange={(value) =>
                updatePropertyField(property.propertyId, "suite", value)
              }
            />
            <div className="grid grid-cols-3 gap-4">
              <InputField
                label="City"
                value={property.city}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "city", value)
                }
              />
              <InputField
                label="State"
                value={property.state}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "state", value)
                }
              />
              <InputField
                label="Zip"
                value={property.zip}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "zip", value)
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InputField
                label="Units"
                value={property.units}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "units", value)
                }
              />
              <InputField
                label="Floors"
                value={property.floors}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "floors", value)
                }
              />
              <InputField
                label="Square feet"
                value={property.sqFt}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "sqFt", value)
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InputField
                label="Acreage"
                value={property.acreage}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "acreage", value)
                }
              />
              <InputField
                label="Year built"
                value={property.yearBuilt}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "yearBuilt", value)
                }
              />
              <InputField
                label="Roof size"
                value={property.roofSize}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "roofSize", value)
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Pays bill"
                value={property.paysBill}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "paysBill", value)
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="BBL"
                value={property.bbl}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "bbl", value)
                }
              />
              <InputField
                label="BIN"
                value={property.bin}
                onChange={(value) =>
                  updatePropertyField(property.propertyId, "bin", value)
                }
              />
            </div>
            <PrimaryButton
              onClick={() => saveProperty(property.propertyId)}
              disabled={isSaving}
            >
              Save property
            </PrimaryButton>
          </div>
        );
      })}
      {propertyForms.length === 0 && (
        <div className="text-xs text-gray-500">No property data available.</div>
      )}
    </div>
  );

  const renderUtilitySection = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-900">
          Utility API connections
        </div>
        {(settings?.utility.uapiLinks ?? []).map((link) => (
          <div key={link.uapiUserInfoId} className="border rounded p-3">
            <div className="text-sm text-gray-900">
              {link.utility ?? "Utility provider"}
            </div>
            <div className="text-xs text-gray-600">
              Status: {link.userStatus ?? "Unknown"} • Account:{" "}
              {link.uapiAccountId ?? "Unavailable"}
            </div>
          </div>
        ))}
        {(settings?.utility.uapiLinks ?? []).length === 0 && (
          <div className="text-xs text-gray-500">
            No Utility API connections found.
          </div>
        )}
      </div>
      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-900">
          Manual utility uploads
        </div>
        {(settings?.utility.manualRecords ?? []).map((record) => (
          <div key={record.uapiManualId} className="border rounded p-3">
            <div className="text-sm text-gray-900">
              {record.propertyName ?? `Property ${record.propertyId}`}
            </div>
            <div className="text-xs text-gray-600">
              Electric: {record.estimatedMonthlyElectric ?? "N/A"} • Gas:{" "}
              {record.estimatedMonthlyGas ?? "N/A"}
            </div>
          </div>
        ))}
        {(settings?.utility.manualRecords ?? []).length === 0 && (
          <div className="text-xs text-gray-500">
            No manual utility uploads found.
          </div>
        )}
      </div>
    </div>
  );

  const renderIntegrationsSection = () => (
    <div className="space-y-3">
      {(settings?.integrations ?? []).map((integration) => (
        <div key={integration.userIntegrationId} className="border rounded p-3">
          <div className="text-sm font-semibold text-gray-900">
            Integration #{integration.userIntegrationId}
          </div>
          <div className="text-xs text-gray-600">
            Partner: {integration.partnerId ?? "Unknown"} · System:{" "}
            {integration.systemId ?? "Unknown"}
          </div>
        </div>
      ))}
      {(settings?.integrations ?? []).length === 0 && (
        <div className="text-xs text-gray-500">No integrations connected.</div>
      )}
    </div>
  );

  const renderBillingSection = () => (
    <div className="text-xs text-gray-600">
      {settings?.billing.statusMessage ?? "Billing details are not available."}
    </div>
  );

  const renderSupportSection = () => (
    <ul className="space-y-2">
      {(settings?.support.links ?? []).map((link) => {
        const email = mailtoAddress(link.url);
        return (
          <li key={link.url}>
            <a
              href={link.url}
              className="text-sm text-zeos-orange-dk hover:underline"
              rel="noreferrer"
            >
              {link.label}
            </a>
            {email && (
              <span className="ph-mask ml-2 text-sm text-gray-600">{email}</span>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderAdminSection = () => <AdminPanel />;

  useEffect(() => {
    if (isSuperAdmin === false && activeSection === "admin") {
      setActiveSection("profile");
      setSearchParams({ section: "profile" });
    }
  }, [activeSection, isSuperAdmin, setSearchParams]);

  const sectionContent = () => {
    if (isLoading) {
      return <div className="text-sm text-gray-500">Loading settings...</div>;
    }
    switch (activeSection) {
      case "profile":
        return renderProfileSection();
      case "account":
        return renderAccountSection();
      case "security":
        return renderSecuritySection();
      case "notifications":
        return renderNotificationsSection();
      case "preferences":
        return renderPreferencesSection();
      case "company":
        return renderCompanySection();
      case "properties":
        return renderPropertiesSection();
      case "utility":
        return renderUtilitySection();
      case "integrations":
        return renderIntegrationsSection();
      case "billing":
        return renderBillingSection();
      case "support":
        return renderSupportSection();
      case "admin":
        return renderAdminSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader />
      <main className="bg-zeos-white-md p-4 flex-grow md:p-6">
        <div
          className={`mx-auto space-y-4 ${
            useSuperAdminLayout ? "w-full max-w-none" : "max-w-6xl"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
              <p className="text-sm text-gray-600">
                Manage your profile, preferences, and account settings.
              </p>
            </div>
          </div>

          {renderStatus()}

          <div className="grid grid-cols-12 gap-4 xl:gap-6">
            <aside
              className={
                useSuperAdminLayout
                  ? "col-span-12 lg:col-span-2"
                  : "col-span-3"
              }
            >
              <div className="bg-white border rounded-lg shadow-sm p-3 space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => handleSectionChange(section.key)}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition ${
                      activeSection === section.key
                        ? "bg-zeos-orange-dk text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </aside>
            <section
              className={
                useSuperAdminLayout
                  ? "col-span-12 lg:col-span-10"
                  : "col-span-9"
              }
            >
              <div
                className={
                  activeSection === "admin"
                    ? "space-y-4"
                    : "bg-white border rounded-lg shadow-sm p-6 space-y-4"
                }
              >
                <h2 className="text-lg font-semibold text-gray-900">
                  {sectionLabels[activeSection]}
                </h2>
                {sectionContent()}
              </div>
            </section>
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};

const InputField = ({
  label,
  value,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  testId?: string;
}) => (
  <label className="flex flex-col gap-1 text-sm text-gray-700">
    <span className="text-xs font-semibold text-gray-600">{label}</span>
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      data-testid={testId}
      className={`border rounded px-3 py-2 text-sm ${
        disabled ? "bg-gray-100 text-gray-500" : "bg-white"
      }`}
    />
  </label>
);

const ToggleField = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex items-center justify-between text-sm text-gray-700">
    <span>{label}</span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4"
    />
  </label>
);

const PrimaryButton = ({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="px-4 py-2 bg-zeos-orange-dk text-white rounded text-sm disabled:opacity-60"
  >
    {children}
  </button>
);

const ReadOnlyField = ({
  label,
  value,
  mask,
}: {
  label: string;
  value?: string | null;
  /** Hide the value in PostHog session recordings. */
  mask?: boolean;
}) => (
  <div className="text-sm text-gray-700">
    <div className="text-xs font-semibold text-gray-600">{label}</div>
    <div className={mask ? "ph-mask" : undefined}>{value || "Not available"}</div>
  </div>
);
